import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PhotoFrame from "@/components/PhotoFrame";
import RichText from "@/components/RichText";
import { Icon } from "@/components/Icon";

function AgentBand() {
  return (
    <div className="mt-12 p-8 rounded-3xl bg-navy text-white grid md:grid-cols-2 gap-6 items-center">
      <div>
        <div className="overline text-saffron">Join the network</div>
        <div className="font-display text-2xl font-bold mt-2">Earn while you connect customers with the right lender.</div>
        <p className="text-sm text-slate-300 mt-2">Get your agent code in minutes — track leads, manage payouts and grow your portfolio.</p>
      </div>
      <div className="flex md:justify-end gap-3">
        <Link to="/agent/register"><Button data-testid="agent-signup-cta" className="btn-saffron rounded-full h-12 px-7">Sign up as new agent <ArrowRight className="h-4 w-4 ml-2"/></Button></Link>
        <Link to="/agent/login"><Button className="btn-outline-navy bg-white rounded-full h-12 px-7 text-navy hover:text-white">Existing? Sign in</Button></Link>
      </div>
    </div>
  );
}

function BankNbfcGrid() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/partners").then(r => setItems(r.data || [])).catch(()=>{}); }, []);
  const filtered = useMemo(() => items.filter(p => p.category === "Bank" || p.category === "NBFC"), [items]);
  return (
    <div className="mt-12">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-2xl font-bold text-navy">Our Bank & NBFC partners</h3>
        <div className="text-sm text-ink-muted">{filtered.length} lenders</div>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="banknbfc-grid">
        {filtered.map((p, i) => (
          <motion.div key={p.id} initial={{opacity:0, y:10}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.35, delay:(i%8)*0.04}}
            whileHover={{ y: -4 }} className="card-flat p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-saffron">{p.category}</div>
            {p.logo_url ? <img src={resolveAssetUrl(p.logo_url)} alt={p.name} className="h-12 w-auto mt-2 object-contain"/>
              : <div className="font-display font-bold text-base mt-1 text-navy">{p.name}</div>}
            {p.logo_url && <div className="text-xs text-ink-muted mt-1">{p.name}</div>}
          </motion.div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-ink-muted py-10">No partners yet.</div>}
      </div>
    </div>
  );
}

// Carousel for hero — supports a single image or multiple. Auto-plays every 5s.
function HeroCarousel({ photos, alt }) {
  const [idx, setIdx] = useState(0);
  const count = photos.length;
  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % count), 5000);
    return () => clearInterval(t);
  }, [count]);
  const go = (d) => setIdx(i => (i + d + count) % count);
  return (
    <div className="relative" data-testid="page-hero-carousel">
      <PhotoFrame src={photos[idx]} alt={alt} ratio="4/3"/>
      {count > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous photo" data-testid="hero-carousel-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-[5] h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white shadow-lg backdrop-blur">
            <ChevronLeft className="h-5 w-5 text-navy"/>
          </button>
          <button onClick={() => go(1)} aria-label="Next photo" data-testid="hero-carousel-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-[5] h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white shadow-lg backdrop-blur">
            <ChevronRight className="h-5 w-5 text-navy"/>
          </button>
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-[5]">
            {photos.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Go to photo ${i+1}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-saffron" : "w-2 bg-navy/30 hover:bg-navy/60"}`}/>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Full-screen-width marquee-style divider band on dynamic pages.
// • True 100vw width (breaks out of the page container).
// • Glass finish — navy → saffron gradient with dotted overlay.
// • Heading shown as eyebrow centered on top.
// • Items SCROLL horizontally (marquee) — auto-duplicates for a seamless loop.
function GlassDivider({ heading, logos }) {
  // duplicate items so the CSS-driven marquee animation loops seamlessly (the
  // existing keyframe translates by -50%, so we need exactly 2 copies)
  const loop = logos.length > 0 ? [...logos, ...logos] : [];
  return (
    <section className="mt-14 full-bleed" data-testid="page-divider-band">
      <div className="relative glass-divider overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-35"/>
        <div className="relative py-8 sm:py-10">
          {heading && (
            <div className="flex items-center justify-center mb-6 px-4">
              <span className="h-px w-12 bg-saffron/70"/>
              <span className="mx-4 text-[11px] sm:text-xs font-bold uppercase tracking-[0.28em] text-saffron whitespace-nowrap">{heading}</span>
              <span className="h-px w-12 bg-saffron/70"/>
            </div>
          )}
          {loop.length > 0 && (
            <div className="marquee-wrap overflow-hidden" data-testid="page-divider-marquee">
              <div className="marquee">
                {loop.map((l, i) => (
                  l.image ? (
                    <img key={i} src={resolveAssetUrl(l.image)} alt={l.label || `logo-${i}`}
                      className="h-9 sm:h-11 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"/>
                  ) : (
                    <span key={i} className="text-white font-display font-semibold text-lg sm:text-xl tracking-tight whitespace-nowrap opacity-90 hover:opacity-100 transition-opacity">{l.label}</span>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/pages/${slug}`)
      .then(r => setPage(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[40vh] grid place-items-center text-ink-muted">Loading…</div>;
  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-extrabold text-navy">Page not found</h1>
      <Link to="/" className="mt-6 inline-block"><Button className="btn-outline-navy rounded-full"><ArrowLeft className="h-4 w-4 mr-2"/> Back home</Button></Link>
    </div>
  );

  const isAgentPage = slug === "partners-loan-agent" || slug === "partners-insurance-agent";
  const isBankNbfc = slug === "partners-bank-nbfc";
  // Combine hero_image + gallery into a single photo set for the hero carousel
  const heroPhotos = [page.hero_image, ...((page.gallery || []).filter(Boolean))].filter(Boolean);
  const features = page.features || [];
  // Divider band: show ONLY when admin has explicitly added a heading and/or logos.
  // No defaults — keeps the page clean until content is populated.
  const dividerHeading = (page.divider_heading || "").trim();
  const dividerLogos = (page.divider_logos || []).filter(l => l && (l.label || l.image));
  const showDivider = dividerHeading.length > 0 || dividerLogos.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
      <motion.div initial={{opacity:0, y:12}} animate={{opacity:1, y:0}} transition={{duration:0.5}} className="grid lg:grid-cols-12 gap-10 items-center">
        <div className={heroPhotos.length ? "lg:col-span-6" : "lg:col-span-12"}>
          <span className="eyebrow-bar"><span className="overline">TARA Finserv</span></span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 text-navy">{page.title}</h1>
          {page.subtitle && <RichText html={page.subtitle} variant="subtitle" className="mt-5"/>}
        </div>
        {heroPhotos.length > 0 && (
          <div className="lg:col-span-6"><HeroCarousel photos={heroPhotos} alt={page.title}/></div>
        )}
      </motion.div>

      {showDivider && (
        <GlassDivider heading={dividerHeading} logos={dividerLogos}/>
      )}

      {features.length > 0 && (
        <section className="mt-14">
          <span className="eyebrow-bar"><span className="overline">Highlights</span></span>
          <h2 className="font-display text-3xl font-extrabold mt-3 text-navy">Why this product fits you</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div key={i} initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.4, delay: i*0.06}}
                whileHover={{ y: -4 }} className="card-flat p-6">
                <div className="h-11 w-11 grid place-items-center rounded-2xl bg-saffron text-white"><Icon name={f.icon || "Sparkles"} className="h-5 w-5"/></div>
                <h3 className="font-display text-lg font-bold mt-4 text-navy">{f.title}</h3>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {page.body_html && (
        <article data-testid="dynamic-page-body"
          className="prose prose-slate max-w-none mt-12 prose-headings:font-display prose-headings:font-bold prose-headings:text-navy prose-h3:text-xl prose-li:my-1 prose-strong:text-navy prose-a:text-saffron"
          dangerouslySetInnerHTML={{ __html: page.body_html }}/>
      )}

      {isBankNbfc && <BankNbfcGrid/>}

      {isAgentPage ? <AgentBand/> : (
        <div className="mt-12 p-7 rounded-3xl bg-navy text-white flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <div className="overline text-saffron">Ready to apply?</div>
            <div className="font-display text-2xl font-bold mt-1">Start your application in 4 minutes.</div>
          </div>
          <Button data-testid="dynamic-apply-cta" onClick={() => window.dispatchEvent(new CustomEvent("tara:open-wizard", { detail: { loan_type: page.title } }))} className="btn-saffron rounded-full h-12 px-7">
            Apply now <ArrowRight className="h-4 w-4 ml-2"/>
          </Button>
        </div>
      )}
    </div>
  );
}
